import type { TotemBorderStyle } from '../types'
import { cn } from '../lib/utils'
import { GoingCheckIcon } from './GoingCheckIcon'

// The ring's gradient is painted once and the element rotates (a compositor
// animation); spinning the gradient's angle instead repainted every avatar on
// every frame for as long as it was on screen.
function totemRingStyle(style: TotemBorderStyle): React.CSSProperties {
  switch (style.type) {
    case 'solid': return { background: style.color }
    case 'gradient': return {
      background: `conic-gradient(${style.colors[0]}, ${style.colors[1]}, ${style.colors[0]})`,
    }
    case 'rainbow': return {
      background: 'conic-gradient(#ff3366, #ff8800, #ffcc00, #00cc88, #0088ff, #cc44ff, #ff3366)',
    }
  }
}

export function TotemAvatar({
  emoji,
  imageUrl,
  borderStyle,
  partial,
  dimmed,
  className,
}: {
  emoji: string
  imageUrl?: string
  borderStyle?: TotemBorderStyle
  partial?: boolean
  dimmed?: boolean
  className?: string
}) {
  const showColorBorder = borderStyle && !dimmed
  return (
    <div
      className={cn(
        'relative rounded-full shrink-0',
        showColorBorder ? 'p-[2px]' : 'border-2 bg-secondary',
        dimmed ? 'border-muted-foreground/40' : !showColorBorder && 'border-background',
        className,
      )}
    >
      {showColorBorder && (
        <span
          aria-hidden
          style={totemRingStyle(borderStyle)}
          className={cn('absolute inset-0 rounded-full', borderStyle.type !== 'solid' && 'animate-ring-spin')}
        />
      )}
      {borderStyle ? (
        <div className="relative rounded-full bg-secondary flex items-center justify-center overflow-hidden h-full w-full">
          {imageUrl ? (
            <img src={imageUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <span className="leading-none">{emoji}</span>
          )}
        </div>
      ) : imageUrl ? (
        <img src={imageUrl} alt="" className="h-full w-full rounded-full object-cover" />
      ) : (
        <span className="leading-none">{emoji}</span>
      )}
      {partial && (
        <GoingCheckIcon
          partial
          className="absolute -bottom-0.5 -right-0.5 size-3"
          strokeWidth={3}
        />
      )}
    </div>
  )
}
